import { useContext, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { html } from '@codemirror/lang-html'
import { css } from '@codemirror/lang-css'
import { javascript } from '@codemirror/lang-javascript'
import { githubLight } from '@uiw/codemirror-theme-github'
import { Tabs, Tab, Box, Button } from '@mui/material'
import { ContentCopy, DataObject } from '@mui/icons-material'

import { AuthContext } from '../../context/AuthContext'
import { SessionContext } from '../../context/SessionContext'
import { showToast } from '../commonUnit/Toast'
import { filterProfanity } from '../../tool/profanityFilter'
import { updateTaskInSession } from '../../service/sessionService'
import { updateTestCodeInSession } from '../../service/testService'
import GuiExerciseAiChat from './GuiExerciseAiChat'
import RawDataModal from '../commonUnit/RawDataPanel'
import InstructorDashboardPanel from '../instructorDashboard/InstructorDashboardPanel'
import '../../css/taskcard.scss'

const FILE_LABELS = { html: 'HTML', css: 'CSS', js: 'JavaScript' }
const FILE_EXTENSIONS = { html: [html()], css: [css()], js: [javascript()] }
const EMPTY_STARTER = { html: '', css: '', js: '' }
const ALL_FILES = ['html', 'css', 'js']
// Kept small on purpose: these data URLs are stored inline in session.test_code (fetched
// on every getSessionById poll - SessionContext refetches every 10s), and neither is ever
// sent to the AI (see getInitialFinalCSSSystemPrompt), so there's no accuracy reason to
// keep them large - only clarity for a student comparing them by eye.
const EXERCISE_IMAGE_MAX_WIDTH = 1000
const EXERCISE_IMAGE_QUALITY = 0.7
const EXERCISE_IMAGE_WARN_BYTES = 500 * 1024
// Animated GIFs can't go through the canvas recompression below without losing their
// animation (canvas only ever captures one frame), so they're stored as uploaded instead -
// capped here since nothing downsizes them.
const MAX_GIF_BYTES = 2 * 1024 * 1024

function parseStarter(starterCode) {
  try {
    return { ...EMPTY_STARTER, ...JSON.parse(starterCode || '{}') }
  } catch (e) {
    return { ...EMPTY_STARTER }
  }
}

// Downscales/recompresses an instructor-uploaded image client-side before it ever reaches
// test_code - see EXERCISE_IMAGE_MAX_WIDTH comment above. Used for both the Starter Image
// (InitialFinalCSS's "Your View" starting content) and the Target Image.
function compressImageFile(file) {
  if (file.type === 'image/gif') {
    if (file.size > MAX_GIF_BYTES) {
      return Promise.reject(new Error('That GIF is larger than 2MB - animated GIFs are stored as uploaded (recompressing would lose the animation), so please use a smaller one.'))
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onerror = () => reject(reader.error)
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(file)
    })
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('Could not read that image file.'))
      img.onload = () => {
        if (!img.width || !img.height) {
          reject(new Error('That image has no visible dimensions.'))
          return
        }
        const scale = Math.min(1, EXERCISE_IMAGE_MAX_WIDTH / img.width)
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(img.width * scale))
        canvas.height = Math.max(1, Math.round(img.height * scale))
        const ctx = canvas.getContext('2d')
        // JPEG has no alpha channel - filling white first avoids a transparent PNG/screenshot
        // compositing onto black (the canvas's default empty-pixel color) once re-encoded.
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', EXERCISE_IMAGE_QUALITY))
      }
      img.src = reader.result
    }
    reader.readAsDataURL(file)
  })
}

// Rewrites the first <img>'s src in an HTML document string, or inserts a minimal default
// document if there's no <img> tag yet (e.g. the instructor cleared the HTML entirely) -
// lets the Starter Image upload below swap in an instructor's own image without requiring
// them to hand-edit the HTML.
function setImgSrcInHtml(currentHtml, dataUrl) {
  if (/<img\b[^>]*>/i.test(currentHtml)) {
    return currentHtml.replace(/(<img\b[^>]*?\ssrc=")[^"]*(")/i, `$1${dataUrl}$2`)
  }
  return `<!doctype html>\n<html>\n<head>\n  <title>Responsive Image</title>\n</head>\n<body>\n  <img class="hero" src="${dataUrl}" alt="Responsive image exercise">\n</body>\n</html>`
}

// The read side of setImgSrcInHtml - used to show a Starter Image preview thumbnail
// without duplicating the uploaded data URL in separate state (starter.html is already
// the source of truth for it).
function getImgSrcFromHtml(currentHtml) {
  const match = /<img\b[^>]*?\ssrc="([^"]*)"/i.exec(currentHtml || '')
  return match ? match[1] : null
}

/**
 * Instructor-facing editor for GUI (HTML/CSS/JS/Mixed) exercises, rendered
 * from Home.jsx at /session/:id - the same route Python/SQL sessions use for
 * instructor customization (see StarterCode.jsx). Students never see this:
 * they're routed straight to /workspace/:id (Workspace.jsx), which stays a
 * read-only "attempt the exercise" surface.
 *
 * Permission model mirrors StarterCode.jsx: role <= 1 (instructor) can edit
 * and save; role 2 (TA) sees the same content read-only, matching how
 * StarterCode disables its CodeMirror editors for anyone above role 1.
 *
 * All three files (html/css/js) are always editable here, even though
 * guiConfig.enabledFiles restricts which one(s) the *student* edits at
 * /workspace/:id. enabledFiles is a student-view restriction, not an
 * instructor-authoring one - a JS-only exercise still needs an editable HTML
 * scaffold, or an instructor can never change what the student's script runs
 * against (see TYPE_CONFIGS' hardcoded profile-card markup).
 */
// `authoringOnly` hides everything that only makes sense for a session that already has
// students in it: the Monitor tab and its InstructorDashboardPanel, the Distribute link
// and the raw-data modal. The creation wizard sets it, because InstructorDashboardPanel's
// subtree consumes GroupsContext, ErrorContext, PresenterListContext, MessageContext and
// AutoGroupingContext, none of which have a Provider outside /session/:id - reaching it
// from the wizard is an unhandled TypeError, i.e. a blank page. Defaults to false so the
// /session/:id mount at Home.jsx is unchanged.
export default function GuiExerciseEditor({ guiConfig, authoringOnly = false }) {
  const { currentUser } = useContext(AuthContext)
  const { session, setSession } = useContext(SessionContext)

  const [isEditable, setIsEditable] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [assignment, setAssignment] = useState('')
  const [starter, setStarter] = useState(EMPTY_STARTER)
  // Initial/Final Comparison only (guiConfig.layout === 'compare') - the fixed "Target"
  // render's HTML. Deliberately no `css` field here: the target's styling should be
  // written inline (style="...") directly on its own HTML, not via a separate
  // stylesheet - see guiSessionConfig.js's INITIAL_FINAL_SHARED_CSS comment for why
  // (a hidden stylesheet with named class/id hooks let the AI assistant match the
  // answer by string, not by understanding the student's description).
  const [finalCode, setFinalCode] = useState({ html: '', image: '', js: '' })
  const [tests, setTests] = useState([])
  const [tabValue, setTabValue] = useState(0)
  const [rawDataModalOpen, setRawDataModalOpen] = useState(false)
  const [isCompressingImage, setIsCompressingImage] = useState(false)

  if (!session || !guiConfig) return null

  const canEdit = currentUser?.role <= 1
  const enabledFiles = guiConfig.enabledFiles || ['html', 'css', 'js']
  const liveAssignment = session.task || guiConfig.assignment || ''
  const liveTests = guiConfig.checks || guiConfig.defaultChecks || []
  const isCompareLayout = guiConfig.layout === 'compare'
  const isCompareImageLayout = guiConfig.layout === 'compare-image'
  const isCompareJsLayout = guiConfig.layout === 'compare-js'
  // Read from the edit buffer while editing, otherwise from the live saved starter -
  // same isEditable branch every other field on this page uses (see the HTML/CSS/JS
  // CodeMirror values below), so the preview shown here always matches what Save would
  // actually persist.
  const starterImgSrc = isCompareImageLayout
    ? getImgSrcFromHtml(isEditable ? starter.html : parseStarter(guiConfig.starterCode).html)
    : null

  // Only populate the edit buffer when edit mode is entered, never from a
  // useEffect keyed on session/guiConfig - both change identity on every
  // websocket push and the 10s SessionContext poll, which would otherwise
  // reset whatever the instructor is mid-typing.
  const handleEditClick = () => {
    setAssignment(liveAssignment)
    setStarter(parseStarter(guiConfig.starterCode))
    setFinalCode({ html: guiConfig.finalHtml || '', image: guiConfig.finalImage || '', js: guiConfig.finalJs || '' })
    // Seed from the saved custom suite if one exists, otherwise from the
    // phase's default checks (see DEFAULT_CHECKS in guiSessionConfig.js) so
    // the instructor starts from something that already mirrors current
    // pass/fail behavior instead of a blank list.
    setTests(liveTests.map((t) => ({ ...t })))
    setIsEditable(true)
  }

  const handleTestChange = (index, field, value) => {
    setTests((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)))
  }

  const handleAddTest = () => {
    setTests((prev) => [...prev, { name: '', check: 'true' }])
  }

  const handleRemoveTest = (index) => {
    setTests((prev) => prev.filter((_, i) => i !== index))
  }

  const handleCancel = () => {
    setIsEditable(false)
  }

  const handleTargetImageUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file name after a redo
    if (!file) return
    setIsCompressingImage(true)
    try {
      const dataUrl = await compressImageFile(file)
      setFinalCode((prev) => ({ ...prev, image: dataUrl }))
      if (dataUrl.length > EXERCISE_IMAGE_WARN_BYTES) {
        showToast('That target image is still fairly large after compression - consider a smaller/simpler source image.', 'error')
      }
    } catch (err) {
      showToast(err.message || 'Failed to process that image.', 'error')
    } finally {
      setIsCompressingImage(false)
    }
  }

  // InitialFinalCSS only - swaps the instructor's own image into starter.html's <img> src
  // (see setImgSrcInHtml), so they don't have to hand-edit the HTML just to change what
  // the exercise is a picture of.
  const handleStarterImageUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setIsCompressingImage(true)
    try {
      const dataUrl = await compressImageFile(file)
      setStarter((prev) => ({ ...prev, html: setImgSrcInHtml(prev.html, dataUrl) }))
      if (dataUrl.length > EXERCISE_IMAGE_WARN_BYTES) {
        showToast('That image is still fairly large after compression - consider a smaller/simpler source image.', 'error')
      }
    } catch (err) {
      showToast(err.message || 'Failed to process that image.', 'error')
    } finally {
      setIsCompressingImage(false)
    }
  }

  // Points students at /workspace/:id, not window.location.href (this editor lives at
  // /session/:id) - that's the instructor-authoring route, not the student's attempt
  // surface. Plain URL only, no t.ly shortening - see BasicTabs.handleCopyLink for the
  // shortened-link version other session types still use.
  //
  // Distribute lives in the header, always visible/clickable regardless of isEditable,
  // separate from Save/Cancel below - nothing else about it suggests "this doesn't save
  // your edits." An instructor mid-edit (e.g. just uploaded a Starter/Target image) can
  // easily click this thinking it finishes the job, then never click Save - the image
  // was never persisted, and reappears as the untouched default the next time anyone
  // (including the instructor) loads the page, which reads as "the image reset." Warn
  // rather than silently copying a link to content that isn't actually live yet.
  const handleDistribute = async () => {
    if (isEditable) {
      showToast('You have unsaved exercise changes - Save them first, or students won\'t see them.', 'error')
    }
    const workspaceUrl = `${window.location.origin}/workspace/${session.id}`
    try {
      await navigator.clipboard.writeText(workspaceUrl)
      if (!isEditable) {
        showToast('Link copied to clipboard!', 'success', 2000)
      }
    } catch (e) {
      showToast('Failed to copy link', 'error')
    }
  }

  // The AI chat only ever proposes values into these same edit buffers -
  // applying a suggestion is exactly like typing it in by hand, still
  // requires pressing Save. It writes all three files, not just
  // enabledFiles: the student only *edits* enabledFiles, but the other
  // file(s) are the scaffold their code runs against (e.g. the HTML markup
  // a JS-only exercise manipulates), so they need to be editable here too -
  // otherwise rewriting the assignment can never change what the student is
  // actually working with.
  const handleApplyAiSuggestion = (result) => {
    if (result.assignment) setAssignment(result.assignment)
    setStarter((prev) => {
      const next = { ...prev }
      ALL_FILES.forEach((file) => {
        if (result[file]) next[file] = result[file]
      })
      return next
    })
  }

  const handleSave = async () => {
    if (assignment !== filterProfanity(assignment)) {
      showToast('Please do not use profanity.', 'error')
      return
    }

    const trimmedTests = tests.map((t) => ({ name: t.name.trim(), check: t.check.trim() }))
    if (trimmedTests.some((t) => !t.name || !t.check)) {
      showToast('Every test needs a name and a check expression (or remove it).', 'error')
      return
    }
    // Catch syntax errors before saving, so a bad expression fails here
    // instead of silently failing every check for every student at Run
    // Tests time.
    for (const t of trimmedTests) {
      try {
        // eslint-disable-next-line no-new-func
        new Function('htmlDoc', 'css', 'js', `return (${t.check})`)
      } catch (e) {
        showToast(`"${t.name}" has an invalid check expression: ${e.message}`, 'error')
        return
      }
    }

    setIsSaving(true)
    try {
      const starterJson = JSON.stringify(starter)
      const testsJson = JSON.stringify(trimmedTests)
      const nextTestCode = { ...session.test_code, template: starterJson, unit_test: testsJson }
      if (isCompareLayout) {
        nextTestCode.finalTemplate = JSON.stringify({ html: finalCode.html })
      } else if (isCompareImageLayout) {
        nextTestCode.finalTemplate = JSON.stringify({ image: finalCode.image })
      } else if (isCompareJsLayout) {
        nextTestCode.finalTemplate = JSON.stringify({ js: finalCode.js })
      }
      const [taskResult, codeResult] = await Promise.all([
        assignment !== session.task ? updateTaskInSession(session.id, assignment) : Promise.resolve(true),
        updateTestCodeInSession(session.id, nextTestCode),
      ])

      if (taskResult && codeResult) {
        setSession({
          ...session,
          task: assignment,
          test_code: nextTestCode,
        })
        showToast('Exercise updated successfully.', 'success')
        setIsEditable(false)
      } else {
        showToast('Failed to update the exercise.', 'error')
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="gui-exercise-editor">
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}>
        {authoringOnly ? (
          <span />
        ) : (
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label="gui exercise tabs">
            <Tab label="Exercise Editor" />
            <Tab label="Monitor" />
          </Tabs>
        )}
        {!authoringOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <span className="gui-session-link">{window.location.href}</span>
            <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={handleDistribute}>
              Distribute
            </Button>
            <Button variant="outlined" size="small" startIcon={<DataObject />} onClick={() => setRawDataModalOpen(true)}>
              View Student Data
            </Button>
          </Box>
        )}
      </Box>

      {tabValue === 0 && (
        <>
          <div className="gui-exercise-editor-header">
            <h3>GUI Exercise{guiConfig.phase ? ` — ${guiConfig.phase}` : ''}</h3>
            {canEdit && (
              isEditable ? (
                <div className="gui-exercise-editor-actions">
                  <button className="gui-exercise-btn" onClick={handleCancel} disabled={isSaving}>Cancel</button>
                  <button className="gui-exercise-btn gui-exercise-btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              ) : (
                <button className="gui-exercise-btn" onClick={handleEditClick}>Edit Exercise</button>
              )
            )}
          </div>

          {isEditable && (
            <GuiExerciseAiChat
              enabledFiles={enabledFiles}
              current={{ assignment, ...starter }}
              onApply={handleApplyAiSuggestion}
            />
          )}

          <h4 className="gui-exercise-subtitle">Assignment</h4>
          {isEditable ? (
            <textarea
              className="gui-exercise-textarea"
              value={assignment}
              onChange={(e) => setAssignment(e.target.value)}
              rows={8}
            />
          ) : (
            <div className="gui-exercise-description">{liveAssignment}</div>
          )}

          <h4 className="gui-exercise-subtitle">Files</h4>
          <p className="gui-exercise-note">
            The student only edits and submits {enabledFiles.map((f) => FILE_LABELS[f] || f).join(' and ')} below
            (marked &quot;student edits this&quot;). The other file(s) are the scaffold their code runs against -
            editing them changes what the student sees and builds on, so rewriting the assignment usually means
            rewriting these too.
            {isCompareLayout && ' Write the HTML below as a complete document (starting with <!doctype html>, including <html>/<head>/<body>) rather than a body-only fragment - these students haven’t seen HTML with the surrounding boilerplate stripped out, so a fragment would look unfamiliar. The AI assistant is told to always answer with a full document too, so pasting its reply over this starter keeps the same shape.'}
            {isCompareImageLayout && ' The starter HTML below is just a single <img class="hero"> with a fixed pixel width in the CSS - upload your own image with Starter Image just below instead of hand-editing the HTML, it swaps the <img> src for you.'}
            {isCompareJsLayout && ' Unlike the other two Initial/Final types, students can edit all three files here - HTML and CSS are the scaffold the JavaScript runs against, and the starter JS below should be a working but genuinely incomplete version of the target behavior (not broken), so there is a real, describable gap for the student to close.'}
          </p>
          {isCompareImageLayout && (
            <div className="gui-exercise-file">
              <span className="gui-exercise-file-label">Starter Image</span>
              <p className="gui-exercise-note">
                The image (or GIF) students are trying to make responsive - shown non-responsive
                at first (the CSS below fixes it to a pixel width), which is the exercise's
                "before" state. Replaces the &lt;img&gt; in the HTML below automatically.
              </p>
              {isEditable && (
                <>
                  <input type="file" accept="image/*,image/gif" onChange={handleStarterImageUpload} disabled={isCompressingImage} />
                  {isCompressingImage && <span> Compressing…</span>}
                </>
              )}
              {starterImgSrc ? (
                <div style={{ marginTop: 8 }}>
                  <img src={starterImgSrc} alt="Starter preview" style={{ maxWidth: '280px', maxHeight: '200px', border: '1px solid #e5e5e5', borderRadius: 4 }} />
                </div>
              ) : (
                <div className="gui-exercise-note">No starter image set yet.</div>
              )}
            </div>
          )}
          {ALL_FILES.map((file) => (
            <div key={file} className="gui-exercise-file">
              <span className="gui-exercise-file-label">
                {FILE_LABELS[file] || file}
                {enabledFiles.includes(file) && <em className="gui-exercise-file-tag"> — student edits this</em>}
              </span>
              <CodeMirror
                value={isEditable ? starter[file] : parseStarter(guiConfig.starterCode)[file]}
                theme={githubLight}
                extensions={FILE_EXTENSIONS[file] || []}
                height="140px"
                // Starter HTML is read-only for InitialFinalCSS, for now - changes to it are
                // meant to go through the Starter Image upload above (which swaps the <img>
                // src automatically), not hand-editing, per the note above this block.
                editable={isEditable && !(isCompareImageLayout && file === 'html')}
                onChange={(value) => setStarter((prev) => ({ ...prev, [file]: value }))}
              />
            </div>
          ))}

          {isCompareLayout && (
            <>
              <h4 className="gui-exercise-subtitle">Target Render</h4>
              <p className="gui-exercise-note">
                The HTML students are trying to reach - also a complete document, same as the
                starter HTML above. Leave the CSS pane above empty and write this target's
                styling directly inline (e.g. {'<li style="color: red;">'}) on its own HTML
                instead - a hidden stylesheet with named class/id hooks lets the AI assistant
                match the answer by spotting that name in the CSS text, regardless of how well
                the student describes the change, which is why this type avoids CSS files
                entirely. Students can only write HTML and the AI assistant is never shown this
                content, so every visual difference must be something an inline style (or a
                different tag, or added/removed content) can express.
              </p>
              <div className="gui-exercise-file">
                <span className="gui-exercise-file-label">Target HTML</span>
                <CodeMirror
                  value={isEditable ? finalCode.html : (guiConfig.finalHtml || '')}
                  theme={githubLight}
                  extensions={FILE_EXTENSIONS.html}
                  height="140px"
                  editable={isEditable}
                  onChange={(value) => setFinalCode((prev) => ({ ...prev, html: value }))}
                />
              </div>
            </>
          )}

          {isCompareJsLayout && (
            <>
              <h4 className="gui-exercise-subtitle">Target JavaScript</h4>
              <p className="gui-exercise-note">
                The complete script that produces the behavior students are trying to
                reach - rendered live, alongside the SAME starter HTML/CSS as the Initial
                pane above (there's no separate target HTML/CSS: only the JavaScript
                differs). The AI assistant is never shown this content - only the
                student's own description of how the behavior should change. Unlike the
                Target Image used by the CSS exercise, this is a real running script: a
                student who opens their browser's DevTools on the Target pane can read it
                directly. That tradeoff was made deliberately for this type (a live,
                clickable Target is much more useful for a behavior exercise than a static
                recording), but keep it in mind if a class seems to be finding the answer
                that way instead of describing it to the AI.
              </p>
              <div className="gui-exercise-file">
                <span className="gui-exercise-file-label">Target JavaScript</span>
                <CodeMirror
                  value={isEditable ? finalCode.js : (guiConfig.finalJs || '')}
                  theme={githubLight}
                  extensions={FILE_EXTENSIONS.js}
                  height="140px"
                  editable={isEditable}
                  onChange={(value) => setFinalCode((prev) => ({ ...prev, js: value }))}
                />
              </div>
            </>
          )}

          {isCompareImageLayout && (
            <>
              <h4 className="gui-exercise-subtitle">Target Image</h4>
              <p className="gui-exercise-note">
                A screenshot or mockup of the responsive layout students are trying to reach.
                Shown to students as a plain image, not a live-rendered page - unlike the HTML
                version of this exercise (where the "answer" is just a class/id name), the
                answer here is exact CSS values, and a live-rendered target would let a
                student read those out directly via Inspect Element. The AI assistant is
                never shown this image either - only the student's own description of the
                difference and a screenshot of their own current view.
              </p>
              <p className="gui-exercise-note">
                A single still image only shows one width, but students drag their view across
                many - a plain screenshot won't tell them whether they're right at other sizes.
                Two ways to fix that, neither needs any code change here: upload a{' '}
                <strong>GIF</strong> that shows the layout resizing (a short screen recording of
                you dragging your own browser works well), or compose a{' '}
                <strong>montage</strong> - e.g. the layout at a narrow width stacked above the
                same layout wide - into one flat image before uploading.
              </p>
              {isEditable ? (
                <div className="gui-exercise-file">
                  <input type="file" accept="image/*,image/gif" onChange={handleTargetImageUpload} disabled={isCompressingImage} />
                  {isCompressingImage && <span> Compressing…</span>}
                  {finalCode.image && (
                    <div style={{ marginTop: 8 }}>
                      <img src={finalCode.image} alt="Target preview" style={{ maxWidth: '280px', maxHeight: '200px', border: '1px solid #e5e5e5', borderRadius: 4 }} />
                    </div>
                  )}
                </div>
              ) : guiConfig.finalImage ? (
                <img src={guiConfig.finalImage} alt="Target preview" style={{ maxWidth: '280px', maxHeight: '200px', border: '1px solid #e5e5e5', borderRadius: 4 }} />
              ) : (
                <div className="gui-exercise-note">No target image set yet.</div>
              )}
            </>
          )}

          <h4 className="gui-exercise-subtitle">Tests</h4>
          <p className="gui-exercise-note">
            These run automatically when a student selects Run Tests. Each check is a JS expression evaluated
            against the student&apos;s submission: <code>htmlDoc</code> (the parsed page), <code>css</code>, and
            <code> js</code> (raw source text) are in scope, and the expression should return true/false. These are
            structural checks, not behavioral ones — they can confirm the JS <em>contains</em> an event listener,
            for example, but not that clicking the button actually works.
          </p>
          {isEditable ? (
            <>
              <div className="gui-exercise-tests-edit">
                {tests.map((t, i) => (
                  <div key={i} className="gui-exercise-test-row">
                    <input
                      className="gui-exercise-test-name"
                      value={t.name}
                      placeholder="Test name"
                      onChange={(e) => handleTestChange(i, 'name', e.target.value)}
                    />
                    <textarea
                      className="gui-exercise-test-check"
                      value={t.check}
                      placeholder="e.g. /addEventListener\s*\(\s*'click'/i.test(js)"
                      rows={2}
                      onChange={(e) => handleTestChange(i, 'check', e.target.value)}
                    />
                    <button
                      type="button"
                      className="gui-exercise-btn gui-exercise-test-remove"
                      onClick={() => handleRemoveTest(i)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="gui-exercise-btn" onClick={handleAddTest}>+ Add Test</button>
            </>
          ) : (
            <ul className="gui-exercise-tests">
              {liveTests.map((t) => (
                <li key={t.name}>
                  <strong>{t.name}</strong>
                  <code className="gui-exercise-test-code">{t.check}</code>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Kept mounted (hidden via CSS) rather than conditionally rendered - the Session
          Control sub-tab's session timer is local React state with no server-side persistence,
          which would reset every time an instructor switched away from this tab and back
          if the component were unmounted in between. `isActive` tells it whether it's the
          currently-selected top-level tab, so it doesn't poll telemetry in the background
          while the instructor is on Exercise Editor instead. */}
      {!authoringOnly && (
        <div style={{ display: tabValue === 1 ? 'block' : 'none' }}>
          <InstructorDashboardPanel sessionId={session.id} isActive={tabValue === 1} />
        </div>
      )}

      {!authoringOnly && (
        <RawDataModal open={rawDataModalOpen} onClose={() => setRawDataModalOpen(false)} />
      )}
    </div>
  )
}
